import { CoreError, err, ok, type Result } from './result';

/** ISO 4217 alphabetic currency code, always upper case (e.g. `EUR`, `JPY`). */
export type CurrencyCode = string;

/**
 * A monetary amount. `amount` is ALWAYS a safe integer expressed in the currency's
 * minor units (cents for EUR, yen for JPY, fils for KWD). Floats are never stored.
 */
export interface Money {
  readonly amount: number;
  readonly currency: CurrencyCode;
}

/**
 * ISO 4217 minor-unit exponents for every active currency whose exponent is not 2.
 * Everything else defaults to 2. Source: ISO 4217 list one (SIX Group), which is
 * what payment providers use. Note this deliberately differs from CLDR/Intl in a few
 * places (e.g. Intl formats ISK/HUF cash amounts differently), so we never derive the
 * exponent from `Intl.NumberFormat`.
 */
const NON_DEFAULT_EXPONENTS: Readonly<Record<string, number>> = {
  // 0 decimals
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  // 3 decimals
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  // 4 decimals
  CLF: 4,
  UYW: 4,
};

const CURRENCY_RE = /^[A-Z]{3}$/;

/** True if `code` is syntactically an ISO 4217 code (three upper-case letters). */
export function isCurrencyCode(code: unknown): code is CurrencyCode {
  return typeof code === 'string' && CURRENCY_RE.test(code);
}

/** Trims and upper-cases user input; returns `null` if it is not a three-letter code. */
export function normalizeCurrencyCode(input: string): CurrencyCode | null {
  const code = input.trim().toUpperCase();
  return isCurrencyCode(code) ? code : null;
}

function assertCurrency(code: string): void {
  if (!isCurrencyCode(code)) {
    throw new CoreError(
      'INVALID_CURRENCY',
      `Invalid ISO 4217 currency code: ${JSON.stringify(code)}`,
    );
  }
}

/** Number of minor-unit digits for a currency (JPY 0, EUR 2, KWD 3, CLF 4). */
export function currencyExponent(currency: CurrencyCode): number {
  assertCurrency(currency);
  return NON_DEFAULT_EXPONENTS[currency] ?? 2;
}

/**
 * Largest amount (in minor units) accepted for a single expense, payer, split entry or
 * payment: 10^12 (10 billion EUR, 1 trillion JPY). Keeps sums of many items and their
 * conversions well inside the safe integer range. `parseAmount` reports `OUT_OF_RANGE` above it.
 */
export const MAX_AMOUNT_MINOR = 1_000_000_000_000;

/** Creates a `Money`, throwing `CoreError` if the amount is not a safe integer. */
export function money(amount: number, currency: CurrencyCode): Money {
  assertCurrency(currency);
  if (!Number.isSafeInteger(amount)) {
    throw new CoreError(
      'INVALID_AMOUNT',
      `Money amounts must be safe integers in minor units, got ${amount}`,
    );
  }
  // Normalise -0 to 0 so equality and serialisation are predictable.
  return { amount: amount === 0 ? 0 : amount, currency };
}

/** Zero in the given currency. */
export function zero(currency: CurrencyCode): Money {
  return money(0, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new CoreError('CURRENCY_MISMATCH', `Cannot combine ${a.currency} with ${b.currency}`);
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amount + b.amount, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amount - b.amount, a.currency);
}

export function negateMoney(a: Money): Money {
  return money(-a.amount, a.currency);
}

/** Sums a list of amounts that must all be in `currency`. */
export function sumMoney(items: readonly Money[], currency: CurrencyCode): Money {
  return items.reduce<Money>((acc, m) => addMoney(acc, m), zero(currency));
}

export function equalsMoney(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amount === b.amount;
}

/**
 * Plain decimal representation with a dot separator and exactly `exponent` decimals,
 * no grouping: `{1250, EUR}` → `"12.50"`, `{1000, JPY}` → `"1000"`, `{-5, EUR}` → `"-0.05"`.
 * Suitable for URLs, clipboard and `Intl.NumberFormat#format` string input.
 */
export function toDecimalString(m: Money): string {
  const exp = currencyExponent(m.currency);
  const negative = m.amount < 0;
  const digits = Math.abs(m.amount).toString();
  if (exp === 0) return (negative ? '-' : '') + digits;
  const padded = digits.padStart(exp + 1, '0');
  const intPart = padded.slice(0, -exp);
  const fracPart = padded.slice(-exp);
  return `${negative ? '-' : ''}${intPart}.${fracPart}`;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

export type ParseAmountErrorCode =
  'EMPTY' | 'INVALID_FORMAT' | 'TOO_MANY_DECIMALS' | 'NEGATIVE' | 'OUT_OF_RANGE';

export interface ParseAmountError {
  readonly code: ParseAmountErrorCode;
  readonly input: string;
  /** For `TOO_MANY_DECIMALS`: the maximum number of decimals the currency allows. */
  readonly maxDecimals?: number;
}

export interface ParseAmountOptions {
  /**
   * BCP 47 locale of the user (e.g. `es-ES`, `en-GB`). Only used to resolve the
   * ambiguous case of a single separator followed by exactly three digits
   * (`"1.234"`, `"1,234"`): the locale's decimal separator wins.
   */
  readonly locale?: string;
  /** Accept a leading minus sign (refunds). Defaults to `false`. */
  readonly allowNegative?: boolean;
}

const GROUPING_CHARS_RE = /[\s'\u2019\u00a0\u202f]/g;
const HAS_GROUPING_CHAR_RE = /[\s'\u2019\u00a0\u202f]/;

function localeDecimalSeparator(locale: string): '.' | ',' {
  const parts = new Intl.NumberFormat(locale).formatToParts(1.5);
  const decimal = parts.find((p) => p.type === 'decimal')?.value;
  return decimal === ',' ? ',' : '.';
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parses an amount typed by a user, in either English (`1,234.56`) or Spanish
 * (`1.234,56`, `12,5`) conventions. Spaces, NBSP and apostrophes are accepted as
 * grouping separators; a leading/trailing currency symbol or the currency code is ignored.
 *
 * Separator resolution:
 * 1. If both `.` and `,` appear, the last one is the decimal separator.
 * 2. If one of them appears more than once, it is a grouping separator.
 * 3. A single separator not followed by exactly three digits is the decimal separator
 *    (`12,5`, `12.50`, `0.125`). Same if space/apostrophe grouping is also present.
 * 4. A single separator followed by exactly three digits (`1.234`) is ambiguous: we use
 *    `options.locale`'s decimal separator; without a locale it is treated as decimal only
 *    if the currency has 3+ decimals (KWD), otherwise as a thousands separator.
 *
 * Extra trailing zeros beyond the currency's precision are accepted (`12,5000` EUR is 12.50);
 * any other excess precision is an error, never rounded. In the ambiguous case of rule 4,
 * when the locale makes it a decimal the zeros are NOT dropped: `1.500` EUR in `en-US` (or
 * `10,000` in `es-ES`) is `TOO_MANY_DECIMALS` rather than a silent 1.50 / 10.00, since the
 * user may have meant 1500 / 10000. Amounts above `MAX_AMOUNT_MINOR` are `OUT_OF_RANGE`.
 */
export function parseAmount(
  input: string,
  currency: CurrencyCode,
  options: ParseAmountOptions = {},
): Result<Money, ParseAmountError> {
  const exp = currencyExponent(currency);
  const fail = (code: ParseAmountErrorCode, extra: Partial<ParseAmountError> = {}) =>
    err<ParseAmountError>({ code, input, ...extra });

  let s = input.normalize('NFKC').trim();
  if (s === '') return fail('EMPTY');

  // Strip currency symbols and the currency code at either end.
  s = s
    .replace(/\p{Sc}/gu, ' ')
    .replace(new RegExp(`^${escapeRegExp(currency)}|${escapeRegExp(currency)}$`, 'i'), ' ')
    .trim();

  let negative = false;
  if (/^[-\u2212]/.test(s)) {
    negative = true;
    s = s.slice(1).trim();
  } else if (s.startsWith('+')) {
    s = s.slice(1).trim();
  }
  if (s === '') return fail('EMPTY');
  if (!/^[0-9.,\s'\u2019\u00a0\u202f]+$/.test(s) || !/[0-9]/.test(s)) return fail('INVALID_FORMAT');

  const hasOtherGrouping = HAS_GROUPING_CHAR_RE.test(s);
  const dots = (s.match(/\./g) ?? []).length;
  const commas = (s.match(/,/g) ?? []).length;

  let decimalSep: '.' | ',' | null = null;
  /** Rule 4 resolved as a decimal by the locale: trailing zeros may be thousands. */
  let ambiguousDecimal = false;
  if (dots > 0 && commas > 0) {
    decimalSep = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
  } else if (dots + commas === 1) {
    const sep = dots === 1 ? '.' : ',';
    const [before = '', after = ''] = s.split(sep);
    const beforeDigits = before.replace(GROUPING_CHARS_RE, '');
    if (
      after.length !== 3 ||
      hasOtherGrouping ||
      beforeDigits === '' ||
      /^0+$/.test(beforeDigits)
    ) {
      decimalSep = sep;
    } else if (options.locale !== undefined) {
      decimalSep = localeDecimalSeparator(options.locale) === sep ? sep : null;
      ambiguousDecimal = decimalSep !== null;
    } else {
      decimalSep = exp >= 3 ? sep : null;
    }
  }
  // else: zero separators, or one kind repeated → all grouping.

  let intPart: string;
  let fracPart: string;
  if (decimalSep === null) {
    intPart = s;
    fracPart = '';
  } else {
    const idx = s.lastIndexOf(decimalSep);
    intPart = s.slice(0, idx);
    fracPart = s.slice(idx + 1);
    if (intPart.includes(decimalSep)) return fail('INVALID_FORMAT');
  }
  if (!/^[0-9]*$/.test(fracPart)) return fail('INVALID_FORMAT');

  // Validate grouping in the integer part: canonicalise all grouping separators.
  const canonical = intPart.replace(/[.,\s'\u2019\u00a0\u202f]/g, 'G');
  if (canonical.includes('G')) {
    if (!/^\d{1,3}(G\d{3})+$/.test(canonical)) return fail('INVALID_FORMAT');
  } else if (!/^\d*$/.test(canonical)) {
    return fail('INVALID_FORMAT');
  }
  const intDigits = canonical.replace(/G/g, '') || '0';
  if (decimalSep !== null && fracPart === '' && intPart === '') return fail('INVALID_FORMAT');

  const trimmedFrac = ambiguousDecimal ? fracPart : fracPart.replace(/0+$/, '');
  if (trimmedFrac.length > exp) return fail('TOO_MANY_DECIMALS', { maxDecimals: exp });

  const minorStr = (intDigits + trimmedFrac.padEnd(exp, '0')).replace(/^0+(?=\d)/, '');
  const value = Number(minorStr);
  if (!Number.isSafeInteger(value) || value > MAX_AMOUNT_MINOR) return fail('OUT_OF_RANGE');
  if (negative && value !== 0 && options.allowNegative !== true) return fail('NEGATIVE');

  return ok(money(negative ? -value : value, currency));
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export interface FormatMoneyOptions {
  /** How to show the currency. Defaults to `symbol`. */
  readonly currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  /** Sign display, e.g. `exceptZero` for balances. Defaults to `auto`. */
  readonly signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero';
}

type StringNumberFormat = (value: string) => string;

function formatExact(nf: Intl.NumberFormat, m: Money): string {
  // Intl.NumberFormat#format accepts exact decimal strings (ES2023 Intl.NumberFormat v3),
  // which avoids any float conversion of large minor-unit amounts.
  return (nf.format as unknown as StringNumberFormat)(toDecimalString(m));
}

/**
 * Locale-aware currency formatting, e.g. `formatMoney({amount: 123456, currency: 'EUR'}, 'es-ES')`
 * → `"1234,56 €"`. Always shows exactly the ISO 4217 number of decimals.
 */
export function formatMoney(m: Money, locale: string, options: FormatMoneyOptions = {}): string {
  const exp = currencyExponent(m.currency);
  const nf = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: m.currency,
    currencyDisplay: options.currencyDisplay ?? 'symbol',
    signDisplay: options.signDisplay ?? 'auto',
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  });
  return formatExact(nf, m);
}

/**
 * Locale-aware number without a currency symbol, e.g. for pre-filling an input:
 * `{123456, EUR}` in `es-ES` → `"1234,56"`. Grouping can be disabled.
 */
export function formatAmount(
  m: Money,
  locale: string,
  options: { grouping?: boolean } = {},
): string {
  const exp = currencyExponent(m.currency);
  const nf = new Intl.NumberFormat(locale, {
    style: 'decimal',
    useGrouping: options.grouping ?? true,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  });
  return formatExact(nf, m);
}
